import React from 'react';
import _ from 'lodash';
import { Box, Grid, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { getNumberComma } from '../../../utils/numberUtils';

const SalesProfitInfo = ({ orderInfo, project }) => {
  const getPercent = (newValue) => {
    const totalSales = project?.totalSales;
    const numberValue = Number(newValue);
    if (!totalSales || totalSales === 0) return 0;
    return Math.round((numberValue / totalSales) * 100);
  };

  const renderRoProfitPercent = orderInfo?.roProfitAmtPercent || getPercent(orderInfo?.roProfitAmt);
  const renderRoCostPercent = orderInfo?.roCostAmtPercent || getPercent(orderInfo?.roCostAmt);

  const roInternalCosts = orderInfo?.roInternalCosts || (orderInfo?.roPartnerUsrUuid ? [{
    roPartnerUsrUuid: orderInfo?.roPartnerUsrUuid,
    roPartnerUsrNm: orderInfo?.roPartnerUsrNm,
    roInternalAmt: orderInfo?.roInternalAmt,
    roInternalAmtPercent: getPercent(orderInfo?.roInternalAmt),
  }] : null);

  const totalInteralAmt = _.sumBy(roInternalCosts, 'roInternalAmt') || 0;
  const roAdjProfitAmt = Number(orderInfo?.roProfitAmt) - Number(orderInfo?.roCostAmt) - Number(totalInteralAmt);
  const renderRoAdjProfitAmt = orderInfo?.roAdjProfitAmtPercent || getPercent(roAdjProfitAmt);

  // 앞선 컴포넌트와 통일된 스타일
  const labelSx = { 
    fontWeight: 'bold', 
    color: '#000',
    whiteSpace: 'nowrap',
    fontSize: '0.82rem',
    width: '110px', // 데이터 시작 위치 통일
    flexShrink: 0,
    mr: 2
  };

  const valueSx = { 
    fontSize: '0.85rem',
    color: '#000',
    display: 'flex',
    gap: 1
  };

  const rowSx = {
    py: 0.8,
    borderBottom: '1px solid #eee',
    alignItems: 'center',
    display: 'flex',
  };

  return (
    <Box sx={{ px: 3, py: 0.5 }}>
      {/* 매출이익 */}
      <Box sx={rowSx}>
        <Typography sx={labelSx}>매출이익</Typography>
        <Box sx={valueSx}>
          <Typography sx={{ ...valueSx, fontWeight: 'bold' }}>{getNumberComma(orderInfo?.roProfitAmt)}</Typography>
          <Typography sx={valueSx}>({getNumberComma(renderRoProfitPercent)}%)</Typography>
        </Box>
      </Box>

<Box sx={{ ...rowSx, alignItems: 'flex-start', py: roInternalCosts ? 1.2 : 0.8 }}>
  {/* 레이블: 고정 너비를 유지하여 다른 행들과 세로 라인을 맞춤 */}
  <Typography sx={labelSx}>내부거래비용</Typography>
  
  <Box sx={{ flex: 1 }}>
    {/* 데이터가 없을 때 */}
    {!roInternalCosts && (
      <Typography sx={{ ...valueSx, color: '#999', fontStyle: 'italic' }}>
        등록된 내부 거래비용이 없습니다.
      </Typography>
    )}

    {/* 데이터(테이블)가 있을 때: 레이블 옆 동일한 선상에서 시작 */}
    {roInternalCosts && (
      <TableContainer sx={{ 
        border: '1px solid #eee', 
        borderRadius: '4px',
        maxWidth: '800px' // 테이블이 너무 퍼지지 않도록 최대 너비 설정 (조정 가능)
      }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#fafafa' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.75rem', borderBottom: '1px solid #eee' }}>협업본부 영업담당</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.75rem', borderBottom: '1px solid #eee' }}>거래비용</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5, fontSize: '0.75rem', borderBottom: '1px solid #eee' }}>매출액의 %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roInternalCosts.map((row, index) => (
              <TableRow key={row?.roPartnerUsrUuid || index}>
                <TableCell align="center" sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: index === roInternalCosts.length - 1 ? 'none' : '1px solid #eee' }}>
                  {row?.roPartnerUsrNm}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, fontSize: '0.8rem', fontWeight: 'bold', borderBottom: index === roInternalCosts.length - 1 ? 'none' : '1px solid #eee' }}>
                  {getNumberComma(row?.roInternalAmt)}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.5, fontSize: '0.8rem', borderBottom: index === roInternalCosts.length - 1 ? 'none' : '1px solid #eee' }}>
                  {getNumberComma(row?.roInternalAmtPercent)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
</Box>


      {/* 영업비용 */}
      <Box sx={rowSx}>
        <Typography sx={labelSx}>영업비용</Typography>
        <Box sx={valueSx}>
          <Typography sx={{ ...valueSx, fontWeight: 'bold' }}>{getNumberComma(orderInfo?.roCostAmt)}</Typography>
          <Typography sx={valueSx}>({getNumberComma(renderRoCostPercent)}%)</Typography>
        </Box>
      </Box>

      {/* 최종매출이익 (강조 색상 유지하되 밑줄 제거) */}
      <Box sx={{ ...rowSx, borderBottom: 'none' }}>
        <Typography sx={{ ...labelSx, color: '#bf360c' }}>최종매출이익</Typography>
        <Box sx={valueSx}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#bf360c' }}>
            {getNumberComma(orderInfo?.roAdjProfitAmt)}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#bf360c' }}>
            ({getNumberComma(renderRoAdjProfitAmt)}%)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SalesProfitInfo;
